import { type ChangeEvent, useState } from 'react'

import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import * as Icons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { attachmetDateWithTime } from 'helpers/date/attachment-date-with-time'
import { Icons as helpersIcons } from 'helpers/icons'
import { useAddComment } from 'pages/support/service/react-query/useAddComment/useAddComment'
import { useUserProfile } from 'store'

import {
    StyledAttachmentDeleteButton,
    StyledAttachmentDeleteButtonIcon,
    StyledAttachmentDetails,
    StyledAttachmentListItem,
    StyledAttachmentName,
    StyledAttachmentSize,
    StyledAttachmentType,
    StyledBodyBottomLine,
    StyledHeader,
    StyledImage,
    StyledSubject,
    StyledTextarea,
    StyledWrapper
} from './TicketReply.styled'
import avatarPlaceholder from '../../../../../../../../assets/images/user_placeholder.png'
import { Attachment } from '../../../../common/Attachment'
import { StyledBodyBottomButtons } from '../../../CreateTicket/CreateTicket.styled'

interface TicketReplyProps {
    ticketId: string
}

export const TicketReply = ({ ticketId }: TicketReplyProps) => {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const userData = useUserProfile()
    const [text, setText] = useState<string | null>(null)
    const [attachments, setAttachments] = useState<File[]>([])

    const { mutate } = useAddComment()

    const handleSetText = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value)
    }

    const handleAttachFile = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files)
            setAttachments(filesArray)
        }
    }

    const handleRemoveFile = (fileToDelete: File) => {
        setAttachments(attachments.filter(file => file !== fileToDelete))
    }

    const handleAddComment = () => {
        if (ticketId && text) {
            const newComment = new FormData()
            newComment.append('ticket_id', ticketId.toString())
            newComment.append('comment_body', text)
            attachments?.map(file => newComment.append(`attachment[]`, file))

            mutate(newComment, {
                onSuccess: () => {
                },
                onError: error => {
                    if (error instanceof AxiosError) {
                        error.response
                            ? addToast(
                                  'error',
                                  error.response.data.error.message
                              )
                            : addToast(
                                  'error',
                                  t('error:errorSomethingWentWrong')
                              )
                    }
                },
                onSettled: () => {
                    queryClient.invalidateQueries('ticketDetails')
                }
            })
        }
    }

    return (
        <StyledWrapper>
            <StyledHeader>
                {userData?.avatar?.url ? (
                    <StyledImage src={userData?.avatar.url} />
                ) : (
                    <StyledImage src={avatarPlaceholder} />
                )}
                <StyledSubject>Re: Subject</StyledSubject>
            </StyledHeader>
            <StyledTextarea
                onChange={(e: ChangeEvent<HTMLTextAreaElement>): void =>
                    handleSetText(e)
                }
            />
            {attachments.length > 0 ? (
                <>
                    <ul>
                        {attachments.map(attachment => (
                            <StyledAttachmentListItem key={attachment.name}>
                                <Icons.IconFile />
                                <StyledAttachmentType>
                                    {attachment.type.split('/')[1]}
                                </StyledAttachmentType>
                                <StyledAttachmentDetails>
                                    <StyledAttachmentName>
                                        {attachment.name}{' '}
                                        {attachmetDateWithTime(
                                            attachment.lastModified.toString()
                                        )}
                                    </StyledAttachmentName>
                                    <StyledAttachmentSize>
                                        {(
                                            attachment.size /
                                            (1024 * 1024)
                                        ).toFixed(1)}
                                        mb
                                    </StyledAttachmentSize>
                                </StyledAttachmentDetails>
                                <StyledAttachmentDeleteButton
                                    onClick={() => {
                                        handleRemoveFile(attachment)
                                    }}
                                >
                                    <StyledAttachmentDeleteButtonIcon
                                        src={helpersIcons.close}
                                        alt={t('removeFile')}
                                    />
                                </StyledAttachmentDeleteButton>
                            </StyledAttachmentListItem>
                        ))}
                    </ul>
                    <StyledBodyBottomLine />
                </>
            ) : null}

            <StyledBodyBottomButtons>
                <Attachment
                    handleOnChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleAttachFile(e)
                    }
                />
                <Button
                    text={t('submit')}
                    color='blue'
                    width='10'
                    height='3'
                    font='mont-14-normal'
                    clickFn={() => handleAddComment()}
                />
            </StyledBodyBottomButtons>
        </StyledWrapper>
    )
}
